import Api from "../services/AxiosService";
import { unwrap } from "./envelope";
import type { PennRole } from "../types/auth-types/AuthUser";

/**
 * Role row for the admin Roles screen — the embedded `PennRole` plus the admin
 * metadata svc-03 now returns: `description` (notes), `isActive`
 * (deactivated_at IS NULL), and the active user-assignment count.
 */
export type AdminRole = PennRole & {
  description: string | null;
  isActive: boolean;
  usersAssigned: number;
  /** role_master.created_at (ISO-8601). */
  createdAt: string;
};

/** The 9 permission boolean keys (rule §D.1). */
export const PERMISSION_KEYS = [
  "canCreateApplications",
  "canWorkForOthers",
  "canSeePrices",
  "canGenerateQuote",
  "canGenerateRoi",
  "canApproveEngineering",
  "canSwitchRegion",
  "canManageUsers",
  "canManageMasterData",
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export async function listRoles(): Promise<AdminRole[]> {
  const res = await Api.get("/v1/admin/roles");
  return unwrap<AdminRole[]>(res.data);
}

/** Body of PATCH /v1/admin/roles/:id — edit an existing role in place. */
export type UpdateRoleBody = Partial<
  { displayName: string; description: string | null; isActive: boolean } & Record<PermissionKey, boolean>
>;

export async function updateRole(id: number, body: UpdateRoleBody): Promise<AdminRole> {
  const res = await Api.patch(`/v1/admin/roles/${id}`, body);
  return unwrap<AdminRole>(res.data);
}

/**
 * Body of POST /v1/admin/roles — create a custom role (ADR-0011). `displayName`
 * is required; permissions default to false when omitted. `isInternal` marks a
 * staff role (ADR-0021 — create-only; members sign in via the admin app);
 * omitted = external.
 */
export type CreateRoleBody = {
  displayName: string;
  description?: string | null;
  isInternal?: boolean;
} & Partial<Record<PermissionKey, boolean>>;

export async function createRole(body: CreateRoleBody): Promise<AdminRole> {
  const res = await Api.post("/v1/admin/roles", body);
  return unwrap<AdminRole>(res.data);
}

/**
 * DELETE /v1/admin/roles/:id — soft-deactivate a role on `role_master`
 * (sets `deactivated_at = now()` and `deactivated_by = caller`). 404 if the
 * role id is unknown, 400 if it is already deactivated.
 */
export async function deactivateRole(id: number): Promise<{ success: true }> {
  const res = await Api.delete(`/v1/admin/roles/${id}`);
  return unwrap<{ success: true }>(res.data);
}
